import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, Camera, Mic, Sparkles, ArrowRight, RotateCcw, AlertTriangle,
  Loader2, Video,
} from 'lucide-react';
import { fmtMMSS, classNames } from '@/lib/format';
import {
  otcVerify, otcStart, otcDirectUpload, otcSubmit, recoverFinalizedResponse,
} from '@/lib/otcApi';
import { toast } from 'sonner';

// Browser MediaRecorder support varies. We feature-detect once and remember the
// best mimeType the browser actually supports. Chrome / Firefox / Edge support
// video/webm; Safari supports video/mp4; some Linux builds expose
// video/x-matroska. The backend explicitly allows all three.
const CANDIDATE_MIMETYPES = [
  { mimeType: 'video/webm;codecs=vp9,opus', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/webm;codecs=vp8,opus', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/webm', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/mp4', sendAs: 'video/mp4', ext: 'mp4' },
  { mimeType: 'video/x-matroska;codecs=avc1', sendAs: 'video/x-matroska', ext: 'mkv' },
  { mimeType: 'video/x-matroska', sendAs: 'video/x-matroska', ext: 'mkv' },
];

function pickSupportedRecorderProfile() {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return null;
  for (const cand of CANDIDATE_MIMETYPES) {
    try {
      if (window.MediaRecorder.isTypeSupported(cand.mimeType)) return cand;
    } catch { /* ignore */ }
  }
  // Last resort: empty options — let the browser pick its default
  return { mimeType: '', sendAs: 'video/webm', ext: 'webm' };
}

// Modes:
//   precheck  → must grant camera + mic before being allowed to start
//   briefing  → question intro
//   prep      → counting down to record
//   record    → MediaRecorder running
//   uploading → preparing + uploading video to S3
//   review    → recorded + uploaded; can retake or continue
//   submitted → final state

export default function VideoBotSession() {
  const { otc } = useParams();
  const { data, refetch, isLoading } = useQuery({
    queryKey: ['otc-session', otc],
    queryFn: () => otcVerify(otc),
    retry: false,
    // Disable opportunistic refetches — once the candidate is in-flight we do
    // NOT want a focus-change to flip state from 'valid' to 'consumed' and
    // trip the wrong notice screen. We explicitly `refetch()` after each
    // upload, which is the only time we need fresh server state.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  // Pre-flight states
  const [precheckPassed, setPrecheckPassed] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const streamRef = useRef(null);
  const videoElRef = useRef(null);

  // Session in-progress states
  const [assessmentId, setAssessmentId] = useState(null);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState('precheck');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [retakeBudget, setRetakeBudget] = useState({ used: 0, allowed: 0 });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('uploading'); // uploading | saving
  // Sprint 11.7 — Defensive client-side state override. If any backend call
  // returns 410, we stamp this so the early state-gate flips the UI to the
  // appropriate notice screen, regardless of what useQuery's cached data says.
  const [forcedState, setForcedState] = useState(null);  // 'consumed' | 'expired' | 'revoked'
  const tickRef = useRef();
  const recorderRef = useRef(null);
  const recorderProfileRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const initOutRef = useRef(null);
  const retakeIntentRef = useRef(false);

  const state = forcedState || data?.state;
  const qs = data?.questions || [];
  const current = qs[idx];
  const total = qs.length;
  const responses = data?.responses || [];
  const responseByQ = Object.fromEntries(responses.map((r) => [r.question_id, r]));

  // ---- 1) Pre-check: ask for camera + mic permissions before allowing start
  const runPrecheck = async () => {
    setPermissionError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoElRef.current) {
        videoElRef.current.srcObject = stream;
        await videoElRef.current.play().catch(() => {});
      }
      setPrecheckPassed(true);
    } catch (e) {
      setPermissionError(
        'We could not access your camera or microphone. ' +
        'Please allow access in your browser settings and try again.'
      );
      setPrecheckPassed(false);
    }
  };

  // Auto-attach the existing stream to the live preview tile when the recording view mounts
  useEffect(() => {
    if ((mode === 'record' || mode === 'prep' || mode === 'review') && videoElRef.current && streamRef.current) {
      try { videoElRef.current.srcObject = streamRef.current; } catch { /* ignore */ }
    }
  }, [mode]);

  // ---- 2) Start session (after precheck) — also handles resume
  const startSession = async () => {
    try {
      const s = await otcStart(otc);
      setAssessmentId(s.assessment_id);
      // Resume: jump to first un-finalized question (server already told us which are done)
      const firstPending = qs.findIndex((q) => (responseByQ[q.id]?.status || 'pending') !== 'finalized');
      const startIdx = firstPending === -1 ? 0 : firstPending;
      // eslint-disable-next-line no-console
      console.log('[VB] startSession ok', { assessment_id: s.assessment_id, firstPending, startIdx, totalQs: qs.length, responses: Object.keys(responseByQ) });
      setIdx(startIdx);
      setMode('briefing');
    } catch (e) {
      console.error('[VB] startSession failed', e);
      toast.error(e?.response?.data?.detail || 'Could not start the session.');
    }
  };

  // ---- 3) Timer tick
  useEffect(() => {
    if (mode !== 'prep' && mode !== 'record') return;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (mode === 'prep') {
            // Transition to record automatically
            beginRecord();
            return 0;
          }
          if (mode === 'record') {
            stopRecord();
            return 0;
          }
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [mode]);

  // ---- 4) Recording controls
  const beginPrep = () => {
    if (!current) return;
    setSecondsLeft(current.prep_seconds);
    setMode('prep');
  };

  const beginRecord = () => {
    if (!streamRef.current) {
      setPermissionError('Camera stream lost. Please grant permission again.');
      setMode('precheck');
      setPrecheckPassed(false);
      return;
    }
    const profile = pickSupportedRecorderProfile();
    if (!profile) {
      toast.error('Your browser does not support video recording. Please try Chrome, Firefox, Edge, or Safari.');
      return;
    }
    try {
      const options = profile.mimeType ? { mimeType: profile.mimeType } : undefined;
      const mr = new MediaRecorder(streamRef.current, options);
      chunksRef.current = [];
      recorderProfileRef.current = profile;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleRecorderStop;
      recorderRef.current = mr;
      mr.start();
      recordStartRef.current = Date.now();
      setSecondsLeft(current.record_seconds);
      setMode('record');
    } catch (err) {
      toast.error('Could not start recording: ' + (err?.message || 'unknown error'));
    }
  };

  const stopRecord = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const handleRecorderStop = async () => {
    const profile = recorderProfileRef.current || { sendAs: 'video/webm', ext: 'webm' };
    const blob = new Blob(chunksRef.current, { type: profile.sendAs });
    const duration_sec = Math.max(1, Math.round((Date.now() - recordStartRef.current) / 1000));
    // eslint-disable-next-line no-console
    console.log('[VB] handleRecorderStop', { idx, qid: current?.id, bytes: blob.size, duration_sec });
    setMode('uploading');
    setUploadProgress(5);
    setUploadPhase('uploading');
    try {
      const out = await otcDirectUpload(
        otc, current.id, blob, profile.sendAs, duration_sec,
        (pct) => setUploadProgress(Math.max(10, Math.min(95, pct))),
        {
          intent: retakeIntentRef.current ? 'retake' : 'upload',
          onSaving: () => setUploadPhase('saving'),
        },
      );
      setRetakeBudget({ used: out.retakes_used || 0, allowed: current.retake_attempts || 0 });
      setUploadProgress(100);
      retakeIntentRef.current = false;
      // eslint-disable-next-line no-console
      console.log('[VB] directUpload ok', { qid: current.id, out });
      await refetch();
      setMode('review');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Upload failed';
      const status = e?.response?.status;
      console.error('[VB] directUpload FAILED', { qid: current?.id, status, detail, err: e });
      // Transport failure — server may have finalized before the client got 200.
      if (!status) {
        const recovered = await recoverFinalizedResponse(otc, current?.id, { maxAttempts: 4 });
        if (recovered) {
          setRetakeBudget({ used: recovered.retakes_used || 0, allowed: current.retake_attempts || 0 });
          retakeIntentRef.current = false;
          await refetch();
          setMode('review');
          return;
        }
      }
      // 410 from backend = link is no longer 'valid' (consumed | expired | revoked).
      // Stop trying to record/upload — bail to the appropriate notice screen.
      if (status === 410) {
        const reason = String(detail || '').toLowerCase();
        const next = reason.includes('expir') ? 'expired'
          : reason.includes('revok') ? 'revoked'
            : 'consumed';
        console.warn('[VB] link no longer valid → exiting flow:', next);
        // Stop the recorder + release the camera
        try { recorderRef.current?.stop(); } catch (_) { /* ignore */ }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setForcedState(next);
        return;
      }
      if (status === 409 && String(detail).toLowerCase().includes('retake')) {
        toast.error('You have used all of your retake attempts for this question.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Upload failed');
      }
      retakeIntentRef.current = false;
      setMode('briefing');
    }
  };

  const retake = () => {
    retakeIntentRef.current = true;
    setMode('briefing');
  };

  // ---- 5) Navigation between questions
  const goNext = async () => {
    // eslint-disable-next-line no-console
    console.log('[VB] goNext', { idx, total, isLast: idx + 1 >= total });
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setMode('briefing');
    } else {
      try {
        const out = await otcSubmit(otc);
        // eslint-disable-next-line no-console
        console.log('[VB] otcSubmit ok', out);
        setMode('submitted');
        // Stop the camera/mic
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        console.error('[VB] otcSubmit FAILED', { status, detail, err: e });
        // 410 here is unusual but means the link was consumed mid-flight. Treat
        // it as success-ish (responses are already on the server); show the
        // "Thank you" screen instead of letting the user retry recordings.
        if (status === 410) {
          setMode('submitted');
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          return;
        }
        toast.error(detail || 'Submit failed');
      }
    }
  };

  // ---- 6) Render gates: invalid/expired/consumed/no-questions
  if (isLoading) {
    return <CenterMsg icon={Loader2} spin title="Verifying your invitation…" />;
  }
  // Priority #1 — locally-tracked successful submission. We show this BEFORE
  // any state-derived gates so the candidate sees a celebratory "Thank you!"
  // screen instead of "Session already submitted" (which would otherwise win
  // after the verify endpoint refetches and reports state='consumed').
  if (mode === 'submitted') {
    return <CenterMsg icon={CheckCircle2} title="Thank you!" desc="Your Video Bot responses have been submitted. The HR team will reach out with next steps." emerald />;
  }
  if (!data || state === 'invalid') {
    return <CenterMsg icon={AlertTriangle} title="Invalid or unknown invitation" desc="The link you used is not recognised. Please ask the recruiter to send a new one." />;
  }
  if (state === 'expired') {
    return <CenterMsg icon={AlertTriangle} title="Link expired" desc="This invitation has expired. Please request a fresh link from your recruiter." />;
  }
  if (state === 'consumed') {
    return <CenterMsg icon={CheckCircle2} title="Session already submitted" desc="You have already completed this assessment. Your responses are with the HR team." />;
  }
  if (state === 'revoked') {
    return <CenterMsg icon={AlertTriangle} title="Link revoked" desc="This invitation was withdrawn by the recruiter." />;
  }
  if (qs.length === 0) {
    return <CenterMsg icon={AlertTriangle} title="No questions configured" desc="Your interview pipeline does not have any Video Bot questions yet. Please ask the recruiter to configure them." />;
  }

  // ---- 7) Pre-check view
  if (!precheckPassed || !assessmentId) {
    return (
      <div className="hero-glow min-h-screen">
        <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
          <Card className="card-elevated w-full"><CardContent className="p-8 space-y-5" data-testid="videobot-precheck">
            <div className="text-center">
              <Video className="mx-auto h-10 w-10 text-primary" />
              <h1 className="mt-2 text-2xl font-semibold">Device check</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                We need access to your camera and microphone before you start the interview.
              </p>
            </div>
            <div className="rounded-xl border bg-secondary/40 p-4">
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-slate-900">
                <video ref={videoElRef} className="h-full w-full object-cover" muted playsInline autoPlay data-testid="videobot-preview" />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5"><Camera className={`h-3.5 w-3.5 ${precheckPassed ? 'text-emerald-600' : 'text-muted-foreground'}`} /> Camera</span>
                <span className="inline-flex items-center gap-1.5"><Mic className={`h-3.5 w-3.5 ${precheckPassed ? 'text-emerald-600' : 'text-muted-foreground'}`} /> Microphone</span>
              </div>
            </div>
            {permissionError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="videobot-precheck-error">
                {permissionError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              {!precheckPassed && (
                <Button onClick={runPrecheck} className="gap-1.5" data-testid="videobot-grant-permission">
                  Grant camera + mic access
                </Button>
              )}
              {precheckPassed && (
                <Button onClick={startSession} className="gap-1.5" data-testid="videobot-start-session">
                  Start interview <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  // ---- 8) Main interview view
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-10 border-b bg-card">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-6 py-3">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground font-bold text-xs">H</div>
          <div className="leading-tight">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Kadel Labs · Video Bot</p>
            <p className="text-xs font-semibold">Guided interview · {total} questions</p>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span data-testid="videobot-counter">Question <span className="font-semibold text-foreground">{idx + 1}</span> of {total}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-6 px-6 py-8 lg:grid-cols-[1fr_280px]">
        <Card className="card-elevated"><CardContent className="p-7">
          <PhaseStrip phase={mode} prep={current.prep_seconds} rec={current.record_seconds} />
          <div className="mt-5 flex items-start justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Question {idx + 1} of {total}</p>
            {(mode === 'prep' || mode === 'record') && (
              <span data-testid="videobot-timer" className={classNames('rounded-full border px-3 py-1 text-sm font-semibold tabular-nums', mode === 'record' ? 'border-rose-300 bg-rose-50 text-rose-700' : 'bg-secondary')}>
                {fmtMMSS(secondsLeft)}
              </span>
            )}
          </div>
          <h1 className="mt-2 text-xl font-semibold leading-snug">{current.text}</h1>

          {/* Stage */}
          <div className="mt-5 aspect-video w-full overflow-hidden rounded-xl border bg-gradient-to-br from-slate-900 to-slate-700 grid place-items-center text-white">
            {mode === 'briefing' && (
              <div className="px-8 text-center">
                <Sparkles className="mx-auto h-8 w-8 text-primary-foreground/80" />
                <p className="mt-3 text-base font-medium">Take a moment to read the question above.</p>
                <p className="mt-1 text-sm text-white/70">You will have <span className="font-semibold">{current.prep_seconds}s</span> to prepare and up to <span className="font-semibold">{current.record_seconds}s</span> to answer.</p>
                {current.retake_attempts > 0 && (
                  <p className="mt-2 text-xs text-white/60">Retakes available: <span className="font-semibold">{Math.max(0, current.retake_attempts - retakeBudget.used)}</span> of {current.retake_attempts}</p>
                )}
              </div>
            )}
            {mode === 'prep' && (
              <div className="text-center">
                <p className="text-sm text-white/70">Preparation</p>
                <p className="mt-1 text-5xl font-semibold tabular-nums">{fmtMMSS(secondsLeft)}</p>
              </div>
            )}
            {mode === 'record' && (
              <div className="relative h-full w-full">
                <video ref={videoElRef} className="h-full w-full object-cover" muted playsInline autoPlay />
                <div className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-full bg-rose-500/90 px-3 py-1 text-xs font-medium text-white">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> Recording {fmtMMSS(secondsLeft)}
                </div>
              </div>
            )}
            {mode === 'uploading' && (
              <div className="px-8 text-center w-full">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary-foreground/80" />
                <p className="mt-2 text-base font-medium">
                  {uploadPhase === 'saving' ? 'Saving your response…' : 'Uploading your response…'}
                </p>
                <div className="mx-auto mt-3 h-1.5 w-2/3 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full bg-primary-foreground transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
            {mode === 'review' && (
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                <p className="mt-2 text-base font-medium">Response captured</p>
                <p className="mt-1 text-xs text-white/60">
                  {retakeBudget.allowed > 0
                    ? `You can retake ${Math.max(0, retakeBudget.allowed - retakeBudget.used)} more time${retakeBudget.allowed - retakeBudget.used === 1 ? '' : 's'}.`
                    : 'Retakes are not permitted for this question.'}
                </p>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {mode === 'briefing' && 'When you are ready, start the preparation timer.'}
              {mode === 'prep' && 'Use this time to think — recording starts automatically.'}
              {mode === 'record' && 'Speak naturally. Be concise and structured.'}
              {mode === 'uploading' && (uploadPhase === 'saving' ? 'Saving on our servers — please keep this tab open.' : 'Please keep this tab open.')}
              {mode === 'review' && 'Happy with your answer? Continue to the next question.'}
            </p>
            <div className="flex items-center gap-2">
              {mode === 'briefing' && (
                <Button onClick={beginPrep} data-testid="videobot-start-prep-button" className="gap-1.5">
                  Start preparation <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              {mode === 'review' && retakeBudget.allowed - retakeBudget.used > 0 && (
                <Button variant="outline" onClick={retake} className="gap-1.5" data-testid="videobot-retake-button"><RotateCcw className="h-4 w-4" /> Retake</Button>
              )}
              {mode === 'review' && (
                <Button onClick={goNext} className="gap-1.5" data-testid="videobot-next-button">
                  {idx + 1 < total ? 'Next question' : 'Finish & submit'} <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent></Card>

        {/* Sidebar */}
        <div className="space-y-4 lg:sticky lg:top-20 h-fit">
          <Card className="card-elevated"><CardContent className="p-4" data-testid="videobot-stepper">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your interview</p>
            <ol className="mt-3 space-y-1.5">
              {qs.map((q, i) => {
                const r = responseByQ[q.id];
                const done = r?.status === 'finalized';
                const isActive = i === idx;
                return (
                  <li key={q.id} className={classNames('flex items-start gap-2.5 rounded-md p-2 text-xs', isActive ? 'bg-primary/10 text-primary' : 'text-foreground/70')}>
                    <span className={classNames('mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[10px] font-semibold', done ? 'border-emerald-500 bg-emerald-500 text-white' : isActive ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}>
                      {done ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                    </span>
                    <span className="line-clamp-2 leading-snug">{q.text}</span>
                  </li>
                );
              })}
            </ol>
          </CardContent></Card>
        </div>
      </main>
    </div>
  );
}

function PhaseStrip({ phase, prep, rec }) {
  const stages = [
    { key: 'briefing', label: 'Briefing' },
    { key: 'prep', label: `Prep · ${prep}s` },
    { key: 'record', label: `Record · ${rec}s` },
    { key: 'uploading', label: 'Upload' },
    { key: 'review', label: 'Review' },
  ];
  const order = stages.findIndex((s) => s.key === phase);
  return (
    <ol className="flex items-center gap-2 text-xs">
      {stages.map((s, i) => {
        const done = i < order;
        const active = i === order;
        return (
          <React.Fragment key={s.key}>
            <li className="flex shrink-0 items-center gap-2">
              <span className={classNames('grid h-5 w-5 place-items-center rounded-full text-[10px] font-semibold', done ? 'bg-emerald-500 text-white' : active ? 'bg-primary text-primary-foreground ring-4 ring-primary/15' : 'bg-slate-200 text-slate-500')}>
                {done ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
              </span>
              <span className={classNames('text-xs', active ? 'font-semibold' : 'text-muted-foreground')}>{s.label}</span>
            </li>
            {i < stages.length - 1 && <span className="h-px w-5 shrink-0 bg-border" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function CenterMsg({ icon: Icon, title, desc, spin = false, emerald = false }) {
  return (
    <div className="hero-glow min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
        <Card className="card-elevated"><CardContent className="p-8 text-center" data-testid="videobot-status-card">
          <Icon className={classNames('mx-auto h-10 w-10', spin && 'animate-spin', emerald ? 'text-emerald-500' : 'text-muted-foreground')} />
          <h1 className="mt-3 text-2xl font-semibold">{title}</h1>
          {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
        </CardContent></Card>
      </div>
    </div>
  );
}
